/**
 * The campaigns Morcast runs, for creators to choose from: open now, starting soon, and past
 * campaigns with their outcome, optionally one platform's only. Anyone can browse them.
 */

import { ArrowUpRight, Clock, Link2, Megaphone, Rocket } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";

import {
  type CampaignPhase,
  campaignPath,
  type PublicCampaign,
  useCampaigns,
} from "../../campaigns/campaigns.js";
import { PhaseBadge } from "../../campaigns/PhaseBadge.js";
import { creatorsPool, elapsedShare, timingLine } from "../../campaigns/timing.js";
import { BrandMark, PlatformMark } from "../../components/marks.js";
import {
  EmptyState,
  ErrorMessage,
  IconTile,
  PageHeader,
  Skeleton,
  styles,
} from "../../components/ui.js";
import { formatAmount, formatCount } from "../../format.js";
import { platformLabel } from "./RequestList.js";

/** The sections of the page, in order, and the phases each shows. */
const GROUPS: { title: string; phases: CampaignPhase[] }[] = [
  { title: "Open now", phases: ["running"] },
  { title: "Starting soon", phases: ["upcoming"] },
  { title: "Past campaigns", phases: ["ended", "settled", "refunded"] },
];

type PlatformChoice = "ALL" | PublicCampaign["platform"];

const PLATFORMS: { value: PlatformChoice; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "X", label: "X" },
  { value: "YOUTUBE", label: "YouTube" },
];

export function BrowseCampaigns() {
  const campaigns = useCampaigns();
  const [platform, setPlatform] = useState<PlatformChoice>("ALL");
  const shown = (campaigns.data ?? []).filter(
    (campaign) => platform === "ALL" || campaign.platform === platform,
  );

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Campaigns"
        title="Campaigns on Morcast"
        documentTitle="Campaigns"
        actions={
          <Link to="/campaigns/new" className={styles.secondaryButton}>
            Start a campaign
          </Link>
        }
      >
        Each budget is already locked in the escrow. Publish content that follows the brief during
        the campaign window. After it ends, Morcast measures organic results and pays creators in
        proportion to them.
      </PageHeader>

      {campaigns.data && campaigns.data.length > 0 && (
        <fieldset className="mt-8">
          <legend className="sr-only">Platform</legend>
          <div className="inline-flex rounded-xl bg-zinc-900/[0.04] p-1 ring-1 ring-zinc-900/[0.06] ring-inset dark:bg-white/[0.04] dark:ring-white/10">
            {PLATFORMS.map((option) => {
              const count = (campaigns.data ?? []).filter(
                (campaign) => option.value === "ALL" || campaign.platform === option.value,
              ).length;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={platform === option.value}
                  onClick={() => setPlatform(option.value)}
                  className="inline-flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-sm font-medium text-zinc-600 transition hover:text-zinc-900 aria-pressed:bg-white aria-pressed:text-zinc-900 aria-pressed:shadow-[0_1px_2px_rgb(24_24_27/0.08),0_0_0_1px_rgb(24_24_27/0.06)] dark:text-zinc-400 dark:hover:text-white dark:aria-pressed:bg-white/10 dark:aria-pressed:text-white dark:aria-pressed:shadow-none"
                >
                  {option.value !== "ALL" && (
                    <PlatformMark platform={option.value} className="size-3.5" />
                  )}
                  {option.label}{" "}
                  <span className="rounded-full bg-zinc-900/[0.06] px-1.5 text-xs text-zinc-500 tabular-nums dark:bg-white/10 dark:text-zinc-400">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      <div className="mt-8">
        {campaigns.isPending && <CardsLoading />}
        {campaigns.isError && <ErrorMessage error={campaigns.error} />}
        {campaigns.isSuccess && campaigns.data.length === 0 && <NoCampaignYet />}
        {campaigns.isSuccess && campaigns.data.length > 0 && shown.length === 0 && (
          <EmptyState icon={Megaphone} title="No campaign on this platform yet." />
        )}
        {GROUPS.map((group) => {
          const inGroup = shown.filter((campaign) => group.phases.includes(campaign.phase));
          if (inGroup.length === 0) {
            return null;
          }
          const titleId = `group-${group.phases[0]}`;
          return (
            <section key={group.title} aria-labelledby={titleId} className="mt-12 first:mt-0">
              <h2 className="flex items-center gap-2.5 font-display text-xl font-semibold tracking-[-0.02em] text-zinc-950 dark:text-white">
                <span id={titleId}>{group.title}</span>{" "}
                <span className="rounded-full bg-zinc-900/[0.06] px-2 py-0.5 font-sans text-xs font-medium text-zinc-500 tabular-nums dark:bg-white/10 dark:text-zinc-400">
                  {inGroup.length}
                </span>
              </h2>
              <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {inGroup.map((campaign) => (
                  <CampaignCard key={campaignPath(campaign)} campaign={campaign} />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/** Before the first campaign: what creators and brands can do meanwhile. */
function NoCampaignYet() {
  const nextSteps = [
    {
      to: "/accounts",
      icon: Link2,
      title: "Creators, link your account",
      text: "Link the X account or YouTube channel you publish with, so that you can take part as soon as a campaign opens.",
    },
    {
      to: "/campaigns/new",
      icon: Rocket,
      title: "Brands, start a campaign",
      text: "Sending a request is free. Once Morcast accepts it and you deposit the budget, creators see it here.",
    },
  ];
  return (
    <div className={`relative overflow-hidden px-6 py-14 text-center sm:px-12 ${styles.card}`}>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-brand-400/15 blur-3xl dark:bg-white/[0.04]"
      />
      <div className="relative flex justify-center">
        <IconTile icon={Megaphone} size="lg" />
      </div>
      <h2 className="relative mt-6 font-display text-2xl font-semibold tracking-[-0.025em] text-balance text-zinc-950 dark:text-white">
        No campaign is running yet. Check back soon.
      </h2>
      <p className="relative mx-auto mt-3 max-w-xl text-zinc-600 dark:text-zinc-400">
        Campaigns appear here as soon as their brand deposits the budget.
      </p>
      <ul className="relative mx-auto mt-10 grid max-w-3xl gap-4 text-left sm:grid-cols-2">
        {nextSteps.map(({ to, icon: Icon, title, text }) => (
          <li key={to}>
            <Link
              to={to}
              className="group flex h-full gap-4 rounded-2xl bg-zinc-50 p-5 ring-1 ring-zinc-900/[0.07] transition duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_12px_32px_-16px_rgb(65_168_198/0.45)] hover:ring-brand-500/40 dark:bg-white/[0.03] dark:ring-white/10 dark:hover:bg-white/[0.05] dark:hover:shadow-[0_12px_32px_-16px_rgb(0_0_0/0.8)] dark:hover:ring-brand-400/40"
            >
              <Icon
                aria-hidden
                className="mt-0.5 size-5 shrink-0 text-brand-600 dark:text-brand-400"
              />
              <span>
                <span className="font-semibold group-hover:text-brand-700 dark:group-hover:text-brand-400">
                  {title}
                </span>
                <span className="mt-1 block text-sm text-zinc-600 dark:text-zinc-400">{text}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Placeholders in the shape of campaign cards, while the campaigns load. */
function CardsLoading() {
  return (
    <div
      role="status"
      aria-label="Loading campaigns"
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      {[0, 1, 2].map((index) => (
        <div key={index} className={`space-y-4 ${styles.card}`}>
          <div className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-xl" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-full" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-14 rounded-xl" />
            <Skeleton className="h-14 rounded-xl" />
          </div>
          <Skeleton className="h-1.5 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** A figure on a campaign card, in a tile. */
function CardFigure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-xl bg-zinc-900/[0.03] px-3 py-2.5 ring-1 ring-zinc-900/[0.05] ring-inset dark:bg-white/[0.03] dark:ring-white/[0.06]">
      <dt className="text-xs text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="mt-1 font-display font-semibold text-zinc-950 tabular-nums dark:text-white">
        {children}
      </dd>
    </div>
  );
}

/**
 * A campaign as a card linking to its page. Its brand and platform, what it is, what it pays,
 * and how far it has run.
 */
export function CampaignCard({ campaign }: { campaign: PublicCampaign }) {
  const showsProgress = campaign.phase === "running" || campaign.phase === "upcoming";
  return (
    <li>
      <Link
        to={campaignPath(campaign)}
        className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-[0_1px_2px_rgb(24_24_27/0.04),0_6px_24px_-12px_rgb(24_24_27/0.12)] ring-1 ring-zinc-900/[0.07] transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_48px_-18px_rgb(65_168_198/0.45)] hover:ring-brand-500/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:bg-ink-900/80 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.04)] dark:ring-white/[0.08] dark:hover:bg-ink-850/90 dark:hover:shadow-[inset_0_1px_0_rgb(255_255_255/0.06),0_18px_40px_-20px_rgb(0_0_0/0.9)] dark:hover:ring-brand-400/40"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-400/0 to-transparent transition-colors duration-300 group-hover:via-brand-400/70"
        />
        <div className="flex items-start justify-between gap-3">
          <span className="flex min-w-0 items-center gap-3">
            <BrandMark name={campaign.brand.name} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {campaign.brand.name}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                <PlatformMark platform={campaign.platform} className="size-3" />
                {platformLabel(campaign)}
              </span>
            </span>
          </span>
          <PhaseBadge phase={campaign.phase} />
        </div>
        <h3 className="mt-5 font-display text-lg leading-snug font-semibold tracking-[-0.015em] text-balance text-zinc-950 transition-colors group-hover:text-brand-700 dark:text-white dark:group-hover:text-brand-300">
          {campaign.brief.title}
        </h3>
        <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
          {campaign.brief.description}
        </p>
        <dl className="mt-5 grid grid-cols-2 gap-2.5 text-sm">
          <CardFigure label="Creators share up to">
            {formatAmount(creatorsPool(campaign), campaign.token)}
          </CardFigure>
          <CardFigure label="Target">
            {formatCount(campaign.target)}{" "}
            <span className="block truncate font-sans text-xs font-normal text-zinc-500 dark:text-zinc-400">
              {campaign.metric.unit}
            </span>
          </CardFigure>
        </dl>
        <div className="mt-auto pt-5">
          {showsProgress && (
            <div
              aria-hidden
              className="h-1 overflow-hidden rounded-full bg-zinc-900/[0.06] dark:bg-white/[0.08]"
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-brand-600 to-brand-400"
                style={{ width: `${Math.round(elapsedShare(campaign) * 100)}%` }}
              />
            </div>
          )}
          <p className="mt-3 flex items-center justify-between gap-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">
            <span className="flex items-center gap-1.5">
              <Clock aria-hidden className="size-3.5" />
              {timingLine(campaign)}
            </span>
            <ArrowUpRight
              aria-hidden
              className="size-4 text-zinc-400 transition duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-600 dark:group-hover:text-brand-400"
            />
          </p>
        </div>
      </Link>
    </li>
  );
}
